import { Languages } from '@/common/utils/constants';
import { MembershipTypeSectionItemsInterface } from '@/widgets/BecomeAMember/components/Receipt/components/MembershipTypeSection/MembershipTypeSection.types';
import ReceiptDetailsTitleSection from '@/widgets/BecomeAMember/components/Receipt/components/common/ReceiptDetailsTitleSection/ReceiptDetailsTitleSection';
import { calculateIncreasedDate } from '@/widgets/BecomeAMember/components/Receipt/utils/calculateIncreasedDate';
import { determineLocaleDateStringFromTodayIncreasedByTime } from '@/widgets/BecomeAMember/components/Receipt/utils/determineLocaleDate';
import { formatDate } from '@/widgets/BecomeAMember/components/Receipt/utils/formatDate';
import { formatPrice } from '@/common/utils/formatPriceForDisplay';

interface ExtensionDetailsProps extends MembershipTypeSectionItemsInterface {
  lang: Languages;
}

export default function ExtensionDetails({
  currency,
  priceDecimalSeparator = '.',
  translations,
  termData,
  voucherData,
  startDate,
  lang,
}: ExtensionDetailsProps) {
  const isUnlimitedVoucher =
    voucherData && voucherData.voucher_effective_period === 'UNLIMITED';

  const fmt = (p: number | string) => formatPrice(p, priceDecimalSeparator);

  const oldExtensionPrice = (() => {
    if (
      voucherData &&
      voucherData.discount_value &&
      voucherData.voucher_type === 'COMPANY_FEE_SPLIT'
    ) {
      return isUnlimitedVoucher
        ? fmt(voucherData.discounted_base_price ?? '')
        : '';
    }

    return isUnlimitedVoucher && voucherData.discount_value
      ? fmt(voucherData.base_price ?? '')
      : '';
  })();

  const determineExtensionPrice = () => {
    if (termData.extensionType === 'SUBSEQUENT_RATE_DETAIL') {
      return fmt(termData!.subsequentRateDto?.terms?.[0].price ?? 0);
    }

    if (termData.extensionType === 'TERM_EXTENSION') {
      if (voucherData && voucherData.voucher_type === 'COMPANY_FEE_SPLIT') {
        if (isUnlimitedVoucher) {
          return fmt(voucherData.member_amount ?? '');
        } else {
          return fmt(voucherData?.member_amount_without_discount ?? '');
        }
      }

      if (
        voucherData &&
        voucherData.voucher_type === 'DISCOUNT' &&
        isUnlimitedVoucher
      ) {
        return fmt(voucherData.discounted_base_price ?? voucherData.base_price);
      }

      return fmt(termData?.price ?? 0);
    }
  };

  const rowStartDate = (() => {
    if (
      voucherData?.voucher_bonus_period &&
      voucherData.voucher_bonus_period.runtime_extension_type === 'WITH_EXTENSION'
    ) {
      return calculateIncreasedDate({
        amount: voucherData.voucher_bonus_period.term?.value ?? 0,
        unit: voucherData.voucher_bonus_period.term?.unit ?? '',
        startDate: startDate,
      });
    }

    return startDate;
  })();

  const rowEndDate = calculateIncreasedDate({
    amount: termData.termValue as number,
    unit: termData.termUnit as string,
    startDate: rowStartDate as Date,
  });

  const afterInitialTermDate = formatDate(rowEndDate, lang);

  const paymentFrequencyValue = (() => {
    if (termData.extensionType === "SUBSEQUENT_RATE_DETAIL") {
      const subsequentTermPaymentFrequencyValue =
        termData!.subsequentRateDto?.terms?.[0].paymentFrequencyValue;
      const subsequentTermPaymentFrequencyUnit =
        termData!.subsequentRateDto?.terms?.[0].paymentFrequencyUnit;
      return `${subsequentTermPaymentFrequencyValue} ${subsequentTermPaymentFrequencyUnit}`;
    }

    return `${termData?.paymentFrequencyValue} ${termData?.paymentFrequencyUnit}`;
  })();

  const extensionAdditionalText = (() => {
    if (termData.extensionFixedTerm && termData.extensionFixedTerm > 1) {
      const fixedEndDate = determineLocaleDateStringFromTodayIncreasedByTime({
        amount: termData.extensionFixedTerm,
        unit: termData.extensionFixedTermUnit as string,
        startDate: rowEndDate,
        lang: lang,
      });
      return `${afterInitialTermDate} - ${fixedEndDate} ${translations["becomeamember.floatingreceipt.every"] ?? "Every"
        } ${paymentFrequencyValue}`;
    }

    return `${translations["becomeamember.floatingreceipt.after"]} ${afterInitialTermDate} ${translations["becomeamember.floatingreceipt.every"] ?? "Every"
      } ${paymentFrequencyValue}`;
  })();

  return (
    <ReceiptDetailsTitleSection
      title={translations['becomeamember.floatingreceipt.afterinitialterm']}
      currency={currency as string}
      oldPrice={oldExtensionPrice}
      additionalText={extensionAdditionalText}
      price={determineExtensionPrice()}
    />
  );
}
